import Joi from 'joi';

/**
 * Schema to validate status updates for modules that include `endsAt` and `startsAt`.
 * Ensures:
 * 1. The `endsAt` value is not in the past unless the `targetStatus` is `completed`.
 * 2. The `startsAt` value is not in the future.
 * 3. The `status` is not already set to the target status.
 * @param targetStatus - The status to validate against (e.g., "in-progress", "pending", "completed").
 * @returns - The Joi schema for validation.
 */
export const setStatusWithDatesValidationSchema = (targetStatus: string) => {
	return Joi.object({
		endsAt: Joi.date()
			.required()
			.custom((value, helpers) => {
				if (targetStatus !== 'completed' && new Date(value) < new Date()) {
					return helpers.error('date.past', { value });
				}
				return value;
			})
			.messages({
				'date.past': `validations|set-status|cannot-set-${targetStatus}-past-end-date`,
				'any.required': `validations|set-status|ends-at-required`,
				'date.base': `validations|set-status|ends-at-must-be-a-valid-date`,
			}),

		startsAt: Joi.date()
			.required()
			.custom((value, helpers) => {
				if (targetStatus !== 'pending' && new Date(value) > new Date()) {
					return helpers.error('date.future', { value });
				}
				return value;
			})
			.messages({
				'date.future': `validations|set-status|cannot-set-${targetStatus}-future-start-date`,
				'any.required': `validations|set-status|starts-at-required`,
				'date.base': `validations|set-status|starts-at-must-be-a-valid-date`,
			}),

		status: Joi.string()
			.required()
			.custom((value, helpers) => {
				if (value === targetStatus) {
					return helpers.error('any.invalid', { value });
				}
				return value;
			})
			.messages({
				'any.invalid': `validations|set-status|already-${targetStatus}`,
				'any.required': `validations|set-status|status-required`,
			}),
	}).unknown(true);
};
